// @flow
/* eslint arrow-parens:0 */
import type { Action } from 'app/common/redux/types';
import type {
  AnchorExperienceMap,
  ExperienceMap,
  PlacementMountMap,
} from 'app/common/lib/react/experiences/experienceUtils';
import {
  addAnchorExperience,
  getExperienceAnchor,
  isRenderableExperience,
} from 'app/common/lib/react/experiences/experienceUtils';

export type State = {
  anchorExperiences: AnchorExperienceMap,
  eligibleExperiences: ExperienceMap,
  isFetchingAll: boolean,
  mountedPlacements: PlacementMountMap,
};

const initialState = {
  anchorExperiences: {},
  eligibleExperiences: {},
  isFetchingAll: false,
  mountedPlacements: {},
};

const experiences = (state: State = initialState, action: Action) => {
  if (action.type === 'EXPERIENCE_FETCH_ALL') {
    return { ...state, ...action.payload };
  } else if (action.type === 'EXPERIENCE_FETCH_ALL_COMPLETE') {
    const { eligibleExperiences } = action.payload;
    const newAnchorExperiences = {};
    Object.keys(state.mountedPlacements).forEach((placementId) => {
      addAnchorExperience(newAnchorExperiences, eligibleExperiences[parseInt(placementId, 10)]);
    });
    return {
      ...state,
      ...action.payload,
      anchorExperiences: newAnchorExperiences,
    };
  } else if (action.type === 'EXPERIENCE_FETCH_COMPLETE') {
    const { eligibleExperiences, extraContext, placementId } = action.payload;
    const placementMounted = state.mountedPlacements[placementId];
    // isContextual is a boolean; explicitly defining this to avoid it becoming a number
    // for both test purposes and also for clarity
    const isContextual = !!(extraContext && Object.keys(extraContext).length);
    // do not update if there was already a rendereable experience in the placement
    if (!isRenderableExperience(state.eligibleExperiences[placementId])) {
      // contextual experiences are only eligible for a single instance of the placement
      // so we must make sure the placement is mounted if the experience is contextual
      if (!isContextual || (isContextual && placementMounted)) {
        const newEligibleExperiences = Object.assign(
          {},
          state.eligibleExperiences,
          eligibleExperiences,
        );
        const newAnchorExperiences = Object.assign({}, state.anchorExperiences);
        const newExperience = Object.assign({}, newEligibleExperiences[placementId]);
        if (Object.keys(newExperience).length > 0) {
          newExperience.extraContext = extraContext;
          newEligibleExperiences[placementId] = newExperience;
        }
        if (placementMounted) {
          addAnchorExperience(newAnchorExperiences, newExperience);
        }
        return {
          ...state,
          anchorExperiences: newAnchorExperiences,
          eligibleExperiences: newEligibleExperiences,
        };
      }
    }
  } else if (action.type === 'EXPERIENCE_REMOVE') {
    const { placementId, experienceId } = action.payload;
    const experience = state.eligibleExperiences[placementId];
    const newAnchorExperiences = Object.assign({}, state.anchorExperiences);
    const newEligibleExperiences = Object.assign({}, state.eligibleExperiences);
    if (experience && experience.experience_id === experienceId) {
      if (experience.type === PCONST.thrift.ExperienceType.TOOLTIP) {
        delete newAnchorExperiences[getExperienceAnchor(experience)];
      }
      delete newEligibleExperiences[placementId];
    }
    return {
      ...state,
      anchorExperiences: newAnchorExperiences,
      eligibleExperiences: newEligibleExperiences,
    };
  } else if (action.type === 'PLACEMENT_MOUNT') {
    const { placementId } = action.payload;
    const newMountedPlacements = Object.assign({}, state.mountedPlacements);
    newMountedPlacements[placementId] = true;

    const newAnchorExperiences = Object.assign({}, state.anchorExperiences);
    const experience = state.eligibleExperiences[placementId];
    if (experience) {
      addAnchorExperience(newAnchorExperiences, experience);
    }
    return {
      ...state,
      anchorExperiences: newAnchorExperiences,
      mountedPlacements: newMountedPlacements,
    };
  } else if (action.type === 'PLACEMENT_UNMOUNT') {
    const { placementId } = action.payload;
    const newMountedPlacements = Object.assign({}, state.mountedPlacements);
    const newAnchorExperiences = Object.assign({}, state.anchorExperiences);
    const newEligibleExperiences = Object.assign({}, state.eligibleExperiences);
    const experience = newEligibleExperiences[placementId];

    delete newMountedPlacements[placementId];
    if (experience) {
      delete newAnchorExperiences[getExperienceAnchor(experience)];
      if (experience.extraContext && Object.keys(experience.extraContext).length > 0) {
        delete newEligibleExperiences[placementId];
      }
    }

    return {
      ...state,
      anchorExperiences: newAnchorExperiences,
      eligibleExperiences: newEligibleExperiences,
      mountedPlacements: newMountedPlacements,
    };
  }
  return state;
};

export default experiences;
